import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import {
    AssignFinalGradeCertificate,
    AssignFinalGradeCertificateFailure,
    AssignFinalGradeCertificateSuccess,
    BulkAssignFinalGradeCertificates,
    BulkAssignFinalGradeCertificatesFailure,
    BulkAssignFinalGradeCertificatesSuccess,
    BulkUnassignFinalGradeCertificates,
    BulkUnassignFinalGradeCertificatesFailure,
    BulkUnassignFinalGradeCertificatesSuccess,
    UnassignFinalGradeCertificate,
    UnassignFinalGradeCertificateFailure,
    UnassignFinalGradeCertificateSuccess,
} from 'app/library/final-grade/actions';
import { FinalGrade } from 'app/library/final-grade/models/final-grade.model';
import {
    CreatePrivateModuleCertificate,
    CreatePrivateModuleCertificateFailure,
    CreatePrivateModuleCertificateSuccess,
    CreatePublicModuleCertificate,
    CreatePublicModuleCertificateFailure,
    CreatePublicModuleCertificateSuccess,
    FetchModuleCertificate,
    FetchModuleCertificateFailure,
    FetchModuleCertificateSuccess,
    SetModuleCertificate,
    UnsetModuleCertificate,
    UpdateModuleCertificate,
    UpdateModuleCertificateFailure,
    UpdateModuleCertificateSuccess,
    UpdateModuleCertificateTemplate,
    UpdateModuleCertificateTemplateFailure,
    UpdateModuleCertificateTemplateSuccess,
} from 'app/library/module-certificate/actions';
import { ModuleCertificateExtended } from 'app/library/module-certificate/models/module-certificate.extended.model';
import { FetchPublicCertificates, FetchPublicCertificatesFailure, FetchPublicCertificatesSuccess } from 'app/library/public-certificates/actions';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { DeleteCertificate, DeleteCertificateFailure, DeleteCertificateSuccess, UpdateCertificate, UpdateCertificateFailure, UpdateCertificateSuccess } from '../actions';
import { Certificate } from '../models/certificate.model';
import { CertificateState } from '../models/certificate.state';
import { CertificateApiService } from './certificate.api-service';

@Injectable({
    providedIn: 'root',
})
export class CertificateService {
    @Select(CertificateState.getEntities)
    certificate$: Observable<Certificate[]>;

    constructor(
        private _store: Store,
        private _action$: Actions,
        private _actionHelper: NgxsActionHelper,
        private _translateService: TranslateService,
        private _matSnackBar: MatSnackBar,
        private _certificateApiService: CertificateApiService
    ) {}

    getById(id: string): Certificate {
        return CertificateState.getEntity(this._store.selectSnapshot(CertificateState), id);
    }

    async fetchPublicCertificates(): Promise<boolean> {
        this._store.dispatch(new FetchPublicCertificates());

        return await this._actionHelper.race(FetchPublicCertificatesSuccess, FetchPublicCertificatesFailure).toPromise();
    }

    async fetchModuleCertificate(moduleId: string): Promise<ModuleCertificateExtended> {
        this._store.dispatch(new FetchModuleCertificate(moduleId));

        const fetchModuleCertificateResponseAction = await this._action$
            .pipe(ofActionDispatched(FetchModuleCertificateSuccess, FetchModuleCertificateFailure), take(1))
            .toPromise();

        if (fetchModuleCertificateResponseAction instanceof FetchModuleCertificateSuccess) {
            return fetchModuleCertificateResponseAction.payload;
        }

        return null;
    }

    async updateModuleCertificate(moduleCertificate: ModuleCertificateExtended): Promise<ModuleCertificateExtended> {
        this._store.dispatch(new UpdateModuleCertificate(moduleCertificate));

        const updateModuleCertificateResponseAction = await this._action$
            .pipe(ofActionDispatched(UpdateModuleCertificateSuccess, UpdateModuleCertificateFailure), take(1))
            .toPromise();

        if (updateModuleCertificateResponseAction instanceof UpdateModuleCertificateSuccess) {
            return updateModuleCertificateResponseAction.payload;
        }

        return null;
    }

    async updateModuleCertificateTemplate(moduleCertificateTemplate: { id: string; certificateName: string; moduleId?: string }): Promise<Certificate> {
        this._store.dispatch(
            new UpdateModuleCertificateTemplate({
                id: moduleCertificateTemplate.id,
                certificateName: moduleCertificateTemplate.certificateName,
                moduleId: moduleCertificateTemplate.moduleId,
            })
        );

        const updateModuleCertificateResponseAction = await this._action$
            .pipe(ofActionDispatched(UpdateModuleCertificateTemplateSuccess, UpdateModuleCertificateTemplateFailure), take(1))
            .toPromise();

        if (updateModuleCertificateResponseAction instanceof UpdateModuleCertificateTemplateSuccess) {
            return updateModuleCertificateResponseAction.payload;
        }

        return null;
    }

    async assignPrivateCertificate(moduleId: string, file: File, assignmentRule: number): Promise<ModuleCertificateExtended> {
        this._store.dispatch(
            new CreatePrivateModuleCertificate({
                moduleId,
                file,
                assignmentRule,
            })
        );

        const createPrivateModuleCertificateResponseAction = await this._action$
            .pipe(ofActionDispatched(CreatePrivateModuleCertificateSuccess, CreatePrivateModuleCertificateFailure), take(1))
            .toPromise();

        if (createPrivateModuleCertificateResponseAction instanceof CreatePrivateModuleCertificateSuccess) {
            const successMessage = this._translateService.instant('CERTIFICATE.MESSAGE.PRIVATE_TEMPLATE_ASSIGN_SUCCESS');

            // Show the success message
            this._matSnackBar.open(successMessage, 'OK', {
                verticalPosition: 'top',
                duration: SnackBarTime.medium,
            });

            this._store.dispatch(new SetModuleCertificate(createPrivateModuleCertificateResponseAction.payload));

            return createPrivateModuleCertificateResponseAction.payload;
        }

        if (createPrivateModuleCertificateResponseAction instanceof CreatePrivateModuleCertificateFailure) {
            const failureMessage = this._translateService.instant(`ERROR.RESPONSE.${createPrivateModuleCertificateResponseAction.payload.response.error.message}`);

            // Show the failure message
            this._matSnackBar.open(failureMessage, 'OK', {
                verticalPosition: 'top',
                duration: SnackBarTime.medium,
            });
        }

        return null;
    }

    async assignPublicCertificate(moduleId: string, certificate: Certificate, assignmentRule: number): Promise<ModuleCertificateExtended> {
        if (!certificate.isPublic) {
            throw new Error('Can not assign private certificate as public!');
        }

        this._store.dispatch(
            new CreatePublicModuleCertificate({
                moduleId,
                certificate,
                assignmentRule,
            })
        );

        const createPublicModuleCertificateResponseAction = await this._action$
            .pipe(ofActionDispatched(CreatePublicModuleCertificateSuccess, CreatePublicModuleCertificateFailure), take(1))
            .toPromise();

        if (createPublicModuleCertificateResponseAction instanceof CreatePublicModuleCertificateSuccess) {
            const successMessage = this._translateService.instant('CERTIFICATE.MESSAGE.PUBLIC_TEMPLATE_ASSIGN_SUCCESS');

            // Show the success message
            this._matSnackBar.open(successMessage, 'OK', {
                verticalPosition: 'top',
                duration: SnackBarTime.medium,
            });

            this._store.dispatch(new SetModuleCertificate(createPublicModuleCertificateResponseAction.payload));

            return createPublicModuleCertificateResponseAction.payload;
        }

        if (createPublicModuleCertificateResponseAction instanceof CreatePublicModuleCertificateFailure) {
            const failureMessage = this._translateService.instant(`ERROR.RESPONSE.${createPublicModuleCertificateResponseAction.payload.response.error.message}`);

            // Show the failure message
            this._matSnackBar.open(failureMessage, 'OK', {
                verticalPosition: 'top',
                duration: SnackBarTime.medium,
            });
        }

        return null;
    }

    async unassignCertificateTemplate(tempalteId: string): Promise<boolean> {
        this._store.dispatch(new DeleteCertificate(tempalteId));

        return await this._actionHelper
            .race(DeleteCertificateSuccess, DeleteCertificateFailure)
            .pipe(
                tap((success) => {
                    if (success) {
                        this._store.dispatch(new UnsetModuleCertificate());
                    }
                })
            )
            .toPromise();
    }

    async downloadCertificate(certificate: Certificate): Promise<void> {
        await this._certificateApiService.downloadTemplate(certificate).toPromise();
    }

    async renamePrivateCertificate(certificate: Certificate, name: string): Promise<boolean> {
        this._store.dispatch(new UpdateCertificate({ ...certificate, name }));

        return await this._actionHelper.race(UpdateCertificateSuccess, UpdateCertificateFailure).toPromise();
    }

    async downloadFinalGradeCertificate(finalGradeCertificate: { id: string; name: string }): Promise<void> {
        this._certificateApiService.download(finalGradeCertificate).toPromise();
    }

    async assignFinalGradeCertificate(finalGrade: FinalGrade): Promise<boolean> {
        this._store.dispatch(new AssignFinalGradeCertificate(finalGrade));

        return await this._actionHelper.race(AssignFinalGradeCertificateSuccess, AssignFinalGradeCertificateFailure).toPromise();
    }

    async unassignFinalGradeCertificate(finalGrade: FinalGrade): Promise<boolean> {
        this._store.dispatch(new UnassignFinalGradeCertificate(finalGrade));

        return await this._actionHelper.race(UnassignFinalGradeCertificateSuccess, UnassignFinalGradeCertificateFailure).toPromise();
    }

    async bulkAssignFinalGradeCertificates(moduleId: string): Promise<boolean> {
        this._store.dispatch(new BulkAssignFinalGradeCertificates(moduleId));

        return await this._actionHelper.race(BulkAssignFinalGradeCertificatesSuccess, BulkAssignFinalGradeCertificatesFailure).toPromise();
    }

    async bulkUnassignFinalGradeCertificates(moduleId: string): Promise<boolean> {
        this._store.dispatch(new BulkUnassignFinalGradeCertificates(moduleId));

        return await this._actionHelper.race(BulkUnassignFinalGradeCertificatesSuccess, BulkUnassignFinalGradeCertificatesFailure).toPromise();
    }
}
